<template>
  <div class="h5-config-container">
    <div class="fixed-header">
      <p class="hint">
        客户端拉取：<code>GET /api/v1/passthrough/h5-config</code>
        （返回 <code>h5_ads_open</code> + <code>h5_ads_config</code>，与 iOS
        <code>WebConfig.configWithABTestConfig:</code> 一致）
      </p>
      <p class="hint" v-if="row">
        最近更新：{{ row.updatedAt || '—' }} · 配置 ID：<code>{{ row.id }}</code>
      </p>
    </div>

    <el-form v-loading="loading" label-width="160px" class="editor-form">
      <el-form-item label="后台启用">
        <el-switch v-model="form.enabled" />
        <span class="field-tip">关闭后客户端接口固定返回 h5_ads_open=0</span>
      </el-form-item>
      <el-form-item label="h5_ads_open">
        <el-radio-group v-model="form.h5AdsOpen">
          <el-radio :label="0">0（关）</el-radio>
          <el-radio :label="1">1（开）</el-radio>
        </el-radio-group>
        <span class="field-tip">为 1 时 links 不能为空，否则服务端会规范化为 0</span>
      </el-form-item>
      <el-form-item label="h5_ads_config（JSON）" required>
        <el-input
          v-model="form.h5AdsConfigJson"
          type="textarea"
          :rows="22"
          placeholder="编辑 h5_ads_config 对象（不含 h5_ads_open）"
          class="json-editor"
        />
        <div class="toolbar">
          <el-button size="small" @click="formatJson" :disabled="loading">格式化 JSON</el-button>
          <el-button size="small" @click="resetTemplate" :disabled="loading">恢复默认模板</el-button>
        </div>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="loading" @click="submit">保存</el-button>
        <el-button v-if="row" type="danger" :loading="loading" @click="remove">删除配置</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { PassthroughH5ConfigApi, PassthroughH5ConfigRow } from '../../api/passthroughApi';

// 默认模板与 jigsaw-ios AppDelegate.updateAbConfig 本地 mock 保持一致
const DEFAULT_H5_ADS_CONFIG = {
  ad_flag: 'google_ads_iframe',
  links: [
    {
      url: 'https://puzzle.rabigame.fun/r_game/normal-adslot-test/mai-doc/index.html',
      rollout_percent: 100,
      ad_flag: '',
      block_keywords: '#,abouts,privacy,service',
      detect_ready_width_optimized_ads: 1,
      click_rate: 100,
      auto_roam_on_no_ad_click_after_auto_alignment: 1,
      auto_roam_on_no_ad_click_after_auto_alignment_seconds: 5,
      pages_per_session: [48, 64],
      single_page_session_rate: 2,
      base_stay_seconds: 20,
      stay_random_factor: [0.85, 1.35],
      max_duration_minutes: 20,
      inactivity_timeout_minutes: 5,
      exit_rate_per_page: 0,
      ads_type: 'normal_dom',
      ad_container_selector: ['#normalAdSlot'],
      clickable_selector: [
        'a,[onclick]:not(span),[role="button"]:not(span),[role="link"]:not(span),[jsaction]:not(span)',
      ],
      max_iframe_depth: 6,
      min_iframe_area: 1000,
      probe_window_seconds: 20,
    },
  ],
};

export default defineComponent({
  name: 'PassthroughH5Config',
  data() {
    return {
      loading: false,
      row: null as PassthroughH5ConfigRow | null,
      form: {
        enabled: true,
        h5AdsOpen: 1 as 0 | 1,
        h5AdsConfigJson: JSON.stringify(DEFAULT_H5_ADS_CONFIG, null, 2),
      },
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    resetTemplate() {
      this.form.h5AdsConfigJson = JSON.stringify(DEFAULT_H5_ADS_CONFIG, null, 2);
    },
    formatJson() {
      try {
        const obj = JSON.parse(this.form.h5AdsConfigJson);
        this.form.h5AdsConfigJson = JSON.stringify(obj, null, 2);
        ElMessage.success('已格式化');
      } catch (e: any) {
        ElMessage.error('JSON 无法解析：' + (e?.message || ''));
      }
    },
    async init() {
      this.loading = true;
      try {
        const result = await PassthroughH5ConfigApi.list();
        const first = result.list?.[0] ?? null;
        this.row = first;
        if (first) {
          this.form.enabled = first.enabled;
          this.form.h5AdsOpen = (first.h5AdsOpen ? 1 : 0) as 0 | 1;
          this.form.h5AdsConfigJson = JSON.stringify(first.h5AdsConfig || {}, null, 2);
        } else {
          this.resetTemplate();
        }
      } catch (e) {
        ElMessage.error('加载失败');
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      let parsed: unknown;
      try {
        parsed = JSON.parse(this.form.h5AdsConfigJson);
      } catch (e: any) {
        ElMessage.error('JSON 无法解析：' + (e?.message || ''));
        return;
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        ElMessage.warning('h5_ads_config 须为 JSON 对象');
        return;
      }
      this.loading = true;
      try {
        const payload = {
          enabled: this.form.enabled,
          h5AdsOpen: this.form.h5AdsOpen,
          h5AdsConfigJson: JSON.stringify(parsed),
        };
        if (this.row) {
          const updated = await PassthroughH5ConfigApi.update(this.row.id, payload);
          this.row = updated;
          ElMessage.success('已保存');
        } else {
          const created = await PassthroughH5ConfigApi.create(payload);
          this.row = created;
          ElMessage.success('已创建');
        }
        this.form.h5AdsConfigJson = JSON.stringify(
          this.row?.h5AdsConfig || parsed,
          null,
          2
        );
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '保存失败');
      } finally {
        this.loading = false;
      }
    },
    async remove() {
      if (!this.row) return;
      try {
        await ElMessageBox.confirm('确定删除全局 H5 配置？客户端将回落为关闭状态。', '确认', {
          type: 'warning',
        });
      } catch {
        return;
      }
      this.loading = true;
      try {
        await PassthroughH5ConfigApi.remove(this.row.id);
        this.row = null;
        this.form.enabled = true;
        this.form.h5AdsOpen = 1;
        this.resetTemplate();
        ElMessage.success('已删除');
      } catch (e: any) {
        ElMessage.error(e?.response?.data?.message || e?.message || '删除失败');
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>

<style scoped>
.h5-config-container {
  padding: 16px;
  max-width: 960px;
}
.fixed-header {
  margin-bottom: 16px;
}
.hint {
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.field-tip {
  margin-left: 12px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.json-editor {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
}
.toolbar {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}
</style>
